import { useState, useEffect } from 'react'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { MagnifierIcon } from '@/common/icons/general.tsx'
import { IconType } from '@/common/icons/types.ts'
import { EmailIcon } from '@/common/icons/general.tsx'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { UserListQueries } from '@/queries/users/queries.ts'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'

export interface User {
  id: number | null
  email: string
}

interface UserSelectProps {
  onSelect: (selectedUser: User | null) => void
}

const RECORDS_PER_PAGE = 10 as const
const THREE_MINUTES = 1000 * 60 * 3

// const fetchUsers = async (query: string,ProgramId:number|undefined): Promise<User[]> => {
//   try {
//     const response = await axios.get(`users/search_not_in_program/${ProgramId}?search=${query}`)
//     return response.data // Axios automatically parses JSON responses
//   } catch (error) {
//     throw new Error('Failed to fetch users')
//   }
// }

const UserSelect = ({ onSelect }: UserSelectProps) => {
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [query, setQuery] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  // const { data: users = [], refetch } = useQuery({
  //   queryKey: ['users', query],
  //   queryFn: () => fetchUsers(query,selectedProgram?.id),
  //   enabled: false,
  // })

  const { data: users = { data: [] }, refetch } = useQuery(
    UserListQueries.getPaginatedListByProgramId(
      RECORDS_PER_PAGE,
      1,
      selectedProgramYear?.id,
      query,
      {
        enabled: !!selectedProgramYear,
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
      }
    )
  )

  useEffect(() => {
    if (!query.trim()) return // Skip empty query

    setIsTyping(true)
    const timer = setTimeout(() => {
      setIsTyping(false)
      refetch() // Fetch users from API
    }, 500)

    return () => clearTimeout(timer) // Clear timer on each new keystroke
  }, [query])

  useEffect(() => {
    onSelect(selectedUser)
  }, [selectedUser])

  const addUser = (user: User | string | null) => {
    if (typeof user === 'object' && user?.id) {
      setSelectedUser({ id: user.id, email: user.email })
      setQuery(user.email) // Display selected email in input
    } else if (validateEmail(query.trim())) {
      setSelectedUser({ id: null, email: query.trim() })
      setQuery(query.trim()) // Display entered email in input
    }
  }

  function validateEmail(email: string) {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  return (
    <Combobox as="div" onChange={(user: User | string | null) => addUser(user)}>
      <div className="relative">
        <ComboboxButton className="absolute inset-y-0 left-0 flex pl-3 items-center rounded-l-md px-2 focus:outline-none">
          <MagnifierIcon
            type={IconType.Outline}
            className="text-md font-medium"
          />
        </ComboboxButton>
        <ComboboxInput
          className="w-full rounded-md border-0 bg-white py-1.5 pl-10 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6"
          onChange={(event) => {
            setQuery(event.target.value)
            setSelectedUser(null)
          }}
          onBlur={() => {
            if (validateEmail(query.trim())) {
              setSelectedUser({ id: null, email: query.trim() })
            }
          }}
          value={query}
          placeholder="Search or enter email"
          required={!selectedUser}
        />
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {!isTyping &&
            users?.data?.length > 0 &&
            users?.data?.map((user) => (
              <ComboboxOption
                key={user.id}
                value={user}
                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900"
              >
                <span className="block truncate group-data-[selected]:font-semibold">
                  {user.email}
                </span>
                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary-600 group-data-[selected]:flex group-data-[focus]:text-white">
                  <i className="ki-filled ki-check"></i>
                </span>
              </ComboboxOption>
            ))}
          {!isTyping && users?.data?.length === 0 && (
            <ComboboxOption
              key={0}
              value={query}
              className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900"
            >
              <EmailIcon className="w-6" />
              <span className="text-sm text-gray-500">
                No results. Enter email to invite.
              </span>
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  )
}

export default UserSelect
